import { FormField, Select } from "@rothoblaas/design-system";
import { useEffect, useMemo, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import useI18n from "../../../../../hooks/useTranslations";
import type { LockLight } from "../../../../../http/lock_calculator/types";
import { LockGeometryFilter, useLocks } from "../../../../../http/lock_calculator/useLocks";
import { useFirstLockFamily } from "../../hooks/useFirstLockFamily";
import { isHeaderLockApplicable } from "../../model/headerLock";
import type { LockCalculatorFormValues } from "../../model/form";
import styles from "../../LockCalculator.module.css";

type Props = {
  readOnly: boolean;
  isAdmin: boolean;
  onOpenHelp?: (text: string, image?: string) => void;
};

const lockLabel = (lock: LockLight) => lock.description;

/**
 * Optional wider lock for the header side only. Offered for a MIDI lock under a downward vertical
 * load; the candidates come from the backend (same height, wider, header-side rules only).
 */
export const BomHeaderLockField = ({ readOnly, isAdmin, onOpenHelp }: Props) => {
  const { control, setValue } = useFormContext<LockCalculatorFormValues>();
  const { translation: t } = useI18n();
  const verticalLoad = useWatch({ control, name: "loads.vertical" });
  const firstLockItemId = useWatch({ control, name: "connectorPairs.0.lockItemId" });
  const headerLockItemId = useWatch({ control, name: "bom.headerLockItemId" });
  const firstLockFamily = useFirstLockFamily(firstLockItemId, isAdmin);
  const headerOrientation = useWatch({ control, name: "headerMember.orientation" });
  const headerWidth = useWatch({ control, name: "headerMember.width" });
  const headerHeight = useWatch({ control, name: "headerMember.height" });

  const applicable = isHeaderLockApplicable(verticalLoad, firstLockFamily, headerOrientation) && !!firstLockItemId;
  const filter = useMemo<LockGeometryFilter>(
    () => ({
      headerOrientation,
      headerB: headerWidth,
      headerH: headerHeight,
      widerHeaderThanLockItemId: firstLockItemId,
    }),
    [headerOrientation, headerWidth, headerHeight, firstLockItemId],
  );
  const aborter = useRef(new AbortController());
  // Without the filter the endpoint would list every lock: the request is only meaningful when applicable.
  const query = useLocks(
    { limit: 200, page: 1, q: "" },
    aborter.current.signal,
    isAdmin,
    null,
    applicable ? filter : undefined,
  );
  const options = useMemo(() => (applicable ? (query.data?.items ?? []) : []), [applicable, query.data]);

  // A choice that is no longer a candidate (different lock, header resized) must not stay saved. It only clears
  // once the options are final; LockCalculator clears it when the lock is not MIDI or the load is not downward.
  useEffect(() => {
    if (!applicable || !headerLockItemId || query.isLoading || query.data == null) return;
    if (!options.some((o) => o.id === headerLockItemId)) setValue("bom.headerLockItemId", null, { shouldDirty: true });
  }, [applicable, headerLockItemId, options, query.data, query.isLoading, setValue]);

  if (!applicable) return null;

  return (
    <div className={styles.bomConnectionCount}>
      <Controller
        name="bom.headerLockItemId"
        control={control}
        render={({ field }) => (
          <FormField
            label={t.lockCalculatorModule?.bom?.headerLock}
            showHelp={{
              tooltip: t.lockCalculatorModule?.helpTexts?.headerLock,
              onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.headerLock ?? ""),
            }}
          >
            {({ labelId, isInvalid }) => (
              <Select<LockLight, false>
                inputId={labelId}
                value={options.find((o) => o.id === field.value) ?? null}
                options={options}
                getOptionValue={(opt) => opt.id}
                getOptionLabel={lockLabel}
                placeholder={t.lockCalculatorModule.selectPlaceholder}
                isLoading={query.isLoading}
                isDisabled={readOnly}
                isClearable
                isInvalid={isInvalid}
                onChange={(selected) => field.onChange(selected?.id ?? null)}
              />
            )}
          </FormField>
        )}
      />
    </div>
  );
};
